import { useTranslation } from 'react-i18next';
import { isHexColor } from '../../lib/taskColor';
import styles from './TaskPanel.module.css';

// Quick-pick palette = the colours Genba's 工程表 uses for tasks. These are
// domain data (user-chosen task colours), not design-system chrome, so they live
// here rather than in tokens.css and are rendered via inline style. Each preset
// maps to an i18n colour-name key (scheduler.panel.fields.colors.*) so screen
// readers hear "Gray" / 「グレー」 rather than the raw hex.
const PRESETS = ['#CCCCCC', '#FFFF66', '#FFCC99', '#CCFFCC', '#99FF99', '#CCCCFF', '#FFCCFF', '#FF0000'];

const PRESET_NAME_KEYS: Record<string, string> = {
  '#CCCCCC': 'gray',
  '#FFFF66': 'yellow',
  '#FFCC99': 'peach',
  '#CCFFCC': 'paleGreen',
  '#99FF99': 'lightGreen',
  '#CCCCFF': 'lavender',
  '#FFCCFF': 'palePink',
  '#FF0000': 'red',
};

interface ITaskColorPickerProps {
  /** Current `#RRGGBB`, or undefined to inherit the group colour. */
  value?: string;
  onChange: (color: string | undefined) => void;
}

export function TaskColorPicker({ value, onChange }: ITaskColorPickerProps) {
  const { t } = useTranslation();
  const current = isHexColor(value) ? value.toUpperCase() : undefined;
  const isCustom = current !== undefined && !PRESETS.includes(current);

  return (
    <div className={styles.swatchRow} role="group" aria-label={t('scheduler.panel.fields.color')}>
      {/* Default / clear → inherit the group colour. */}
      <button
        type="button"
        className={`${styles.swatch} ${styles.swatchDefault} ${current === undefined ? styles.swatchSelected : ''}`}
        onClick={() => onChange(undefined)}
        title={t('scheduler.panel.fields.colorDefault')}
        aria-label={t('scheduler.panel.fields.colorDefault')}
        aria-pressed={current === undefined}
      >
        <span className={styles.swatchDefaultMark} aria-hidden />
      </button>

      {PRESETS.map((preset) => (
        <button
          key={preset}
          type="button"
          className={`${styles.swatch} ${current === preset ? styles.swatchSelected : ''}`}
          style={{ background: preset }}
          onClick={() => onChange(preset)}
          title={t(`scheduler.panel.fields.colors.${PRESET_NAME_KEYS[preset]}`)}
          aria-label={t(`scheduler.panel.fields.colors.${PRESET_NAME_KEYS[preset]}`)}
          aria-pressed={current === preset}
        />
      ))}

      {/* Custom colour — native picker behind a swatch; shows the picked colour when active. */}
      <label
        className={`${styles.swatch} ${styles.swatchCustom} ${isCustom ? styles.swatchSelected : ''}`}
        style={isCustom ? { background: current } : undefined}
        title={t('scheduler.panel.fields.colorCustom')}
      >
        {!isCustom && <span className={styles.swatchCustomMark} aria-hidden>+</span>}
        <input
          type="color"
          className={styles.swatchCustomInput}
          value={current ?? '#888888'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          aria-label={t('scheduler.panel.fields.colorCustom')}
        />
      </label>
    </div>
  );
}
